import React, { useState, useCallback, useMemo } from "react";

// Utils
import areObjectsEqual from '../../../../utils/areObjectsEqual';

// Constants
const MAX_NAME_LENGTH = 25;
const MAX_DEPTH_LENGTH = 4;

const INITIAL_ERRORS = {
  layerName: false,
  layerDepth: false,
  checkName: false,
};

// API Service
const layerAPI = {
  checkNameUnique: async (name, id, baseImageId) => {
    const encodedName = encodeURIComponent(name);
    const res = await fetch(`/api/layer/checkname/${encodedName}/${id}/${baseImageId}`);
    if (res.status === 200) {
      const data = await res.json();
      return data.checkStatus;
    }
    return false;
  },
};


const Form = ({ handleSubmit, formData, onDiscard, typeCheck, baseImageId }) => {
  const [form, setForm] = useState(formData);
  const [errors, setErrors] = useState(INITIAL_ERRORS);

  // Check for unsaved changes
  const hasUnsavedChanges = useMemo(() => {
    return !areObjectsEqual(formData, form);
  }, [formData, form]);

  // Check if layer name is unique
  const checkNameUnique = useCallback(async (name, id, baseImageId) => {
    const isNameTaken = await layerAPI.checkNameUnique(name, id, baseImageId);
    setErrors(prev => ({ ...prev, checkName: isNameTaken }));
    return isNameTaken;
  }, []);

  // Prevent negative sign input
  const handleKeyDown = useCallback((e) => {
    if (e.key === "-") {
      e.preventDefault();
    }
  }, []);

  // Handle input changes
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;

    // Limit layer_depth to 4 digits
    const processedValue = name === "layer_depth" ? value.slice(0, MAX_DEPTH_LENGTH) : value;

    setForm(prev => ({ ...prev, [name]: processedValue }));
  }, []);

  // Validate form data
  const validateForm = useCallback(() => {
    const newErrors = {
      layerName: !form.layer_name,
      layerDepth: !form.layer_depth,
      checkName: false,
    };

    setErrors(newErrors);
    return !newErrors.layerName && !newErrors.layerDepth;
  }, [form]);

  // Handle form submission
  const handleFormSubmit = useCallback(async () => {
    // Validate form
    if (!validateForm()) {
      return;
    }

    // Check if name is unique
    const id = typeCheck === 'updateItem' ? form.id : 0;
    const isNameTaken = await checkNameUnique(form.layer_name, id, baseImageId);
    if (isNameTaken) {
      return;
    }

    // Submit form
    handleSubmit(form);
  }, [validateForm, typeCheck, form, checkNameUnique, baseImageId, handleSubmit]);

  // Determine if save button should be disabled
  const isSaveDisabled = useMemo(() => {
    return !hasUnsavedChanges;
  }, [hasUnsavedChanges]);


  return (
    <>
      {/* Layer Name Input */}
      <div className="mt-4">
        <label htmlFor="layer_name" className="form-labels">Name</label>
        <input
          type="text"
          name="layer_name"
          id="layer_name"
          onChange={handleChange}
          value={form.layer_name}
          className="form-control inputs"
          maxLength={MAX_NAME_LENGTH}
        />
        {errors.layerName && (
          <span className="form-text text-danger">
            Layer Name is required
          </span>
        )}
        {errors.checkName && (
          <span className="form-text text-danger">
            This Layer Name already exists, please use a different one.
          </span>
        )}
      </div>

      {/* Layer Depth Input */}
      <div className="mt-5">
        <label htmlFor="layer_depth" className="form-labels">Layer Depth</label>
        <input
          type="number"
          name="layer_depth"
          id="layer_depth"
          onChange={handleChange}
          value={form.layer_depth}
          className="form-control inputs"
          min={0}
          max={9999}
          onKeyDown={handleKeyDown}
        />
        {errors.layerDepth && (
          <span className="form-text text-danger">
            Layer Depth is required
          </span>
        )}
      </div>

      {/* Action Buttons */}
      <div className="pt-5">
        <div className="btn btn-secondary form-btn me-4" onClick={onDiscard}>
          Discard
        </div>
        <input
          type="submit"
          value="Save Changes"
          className="btn bg-orange form-btn"
          onClick={handleFormSubmit}
          disabled={isSaveDisabled}
        />
      </div>
    </>
  );
};

export default Form;
